import React, { useEffect, useState } from "react";
import { Field, Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AppInput from "components/Input/AppInput";
import { Box, FormGroup, Grid, makeStyles, Typography } from "@material-ui/core";
import AppButton from "components/CustomButtons/AppButton";
import { WORKER_TYPE } from "config/AppConfig";
import InputUpload from "components/Input/InputUpload";
import { YupValidations } from "utils/validate";
import AppColor from "utilities/AppColor";
import AppCheckbox from "components/Input/AppCheckbox";
import AppConfig, { NDIS_ORIENTATIONS_LINK } from "config/AppConfig";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";

const { PATTERNS } = AppConfig;
const styles = makeStyles(() => ({
  title: {
    display: 'flex',
    margin: '2rem 0 1rem 0'
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: '1rem'
  },
  textDownload: {
    cursor: 'pointer',
    '& span': {
      color: AppColor.blue,
      textDecoration: 'underline',
      marginRight: '0.5rem'
    }

  },
  link: {
    color: '#0056b3',
    textDecoration: 'underline',
    cursor: 'pointer',
    wordBreak: 'break-word'
  },
  inputFile: {
    minHeight: '40px',
    height: '40px'
  },
  inputFileUpload: {
    minHeight: '70px',
    height: '70px'
  },
  iconDelete: {
    cursor: 'pointer',
    marginBottom: '5rem',
    '&:hover ': {
      color: AppColor.pink,
    }
  },
}));

const defaultReferenceRequests = {
  email: '',
  howToKnow: '',
  telephoneNumber: '',
  name: ''
};

// const maxSize = 1024 * 1024 * 2; // binary
const maxSize = 2000000; // decimal
const maskNumber = '999999999999999';

const VerificationForm = (props) => {
  const {
    onSubmit,
    onDeleteFilesVerification,
    workerDetail,
    changeProgressStatus,
    display,
    disabled,
    type,
    showToastDanger
  } = props;

  const { t } = useTranslation();
  const classes = styles();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileDeleteModal, setFileDeleteModal] = useState(null);
  const [initForm, setInitForm] = useState({
    aphraRegistrationNumber: '',
    isCompletedNdisChecking: false,
    referenceRequests: [defaultReferenceRequests, defaultReferenceRequests],
    // registrationNumberFile: null,
    certificationFiles: [],
    aphraRegistrationFiles: [],
    policeCheckFiles: [],
    workingChildrenFiles: [],
    cprFiles: [],
    ndisOrientationModuleFiles: [],
    infectionControlFiles: [],
    carRegistrationFiles: [],
    resumeFiles: [],
    comprehensiveInsuranceFiles: [],
    otherFiles: [],

    certificationList: [],
    aphraRegistrationList: [],
    policeCheckList: [],
    workingChildrenList: [],
    cprList: [],
    ndisOrientationModuleList: [],
    infectionControlList: [],
    carRegistrationList: [],
    resumeList: [],
    comprehensiveInsuranceList: [],
    otherList: [],

    certificationInput: [],
    aphraRegistrationInput: [],
    policeCheckInput: [],
    workingChildrenInput: [],
    cprInput: [],
    ndisOrientationModuleInput: [],
    infectionControlInput: [],
    carRegistrationInput: [],
    resumeInput: [],
    comprehensiveInsuranceInput: [],
    otherInput: []
  });

  const {
    id,
    role: workerType = type,
    aphraRegistrationNumber,
    references: referenceRequests = [defaultReferenceRequests, defaultReferenceRequests],
    completedNdisChecking,
    workerCertificationResponseList,
    workerAphraRegistrationDocResponseList,
    workerPoliceCheckDocResponseList,
    workerWorkingChildrenDocResponseList,
    workerCprDocResponseList,
    workerNdisOrientationModuleResponseList,
    workerInfectionControlResponseList,
    workerCarRegistrationResponseList,
    workerResumeResponseList,
    workerComprehensiveInsuranceResponseList,
    workerOtherResponseList

  } = workerDetail;

  const getTitleDeleteFile = (fieldName) => {
    if (!fieldName) {
      return '';
    }
    switch (fieldName) {
      case 'certificationList':
        return t('title.deleteFileCertification');
      case 'aphraRegistrationList':
        return t('title.deleteFileAphraRegistration');
      case 'policeCheckList':
        return t('title.deleteFilePoliceCheck');
      case 'workingChildrenList':
        return t('title.deleteFileWorkingChildren');
      case 'cprList':
        return t('title.deleteFileCpr');
      case 'ndisOrientationModuleList':
        return t('title.deleteFile', {
          fileName: t("label.ndisOrientationModule")
        });
      case 'infectionControlList':
        return t('title.deleteFile', {
          fileName: t("label.infectionControl")
        });
      case 'carRegistrationList':
        return t('title.deleteFile', {
          fileName: t("label.carRegistration")
        });
      case 'resumeList':
        return t('title.deleteFile', {
          fileName: t("label.resume")
        });
      case 'comprehensiveInsuranceList':
        return t('title.deleteFile', {
          fileName: t("label.comprehensiveInsurance")
        });
      case 'otherList':
        return t('title.deleteFile', {
          fileName: t("label.other")
        });
      default:
        break;
    }
  };
  const isInvalidSize = (list) => {
    if (list) {
      return !!list.find(item => item.file && item.file.size > maxSize);
    }
    return false
  };


  // generate function
  const generateCertificationList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.certificationFile,
          fileUrl: item.certificationFileAws3Link
        }
      })
    }
    return [];
  }
  const generateAphraRegistrationList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.aphraRegistrationFile,
          fileUrl: item.aphraRegistrationFileAws3Link
        }
      })
    }
    return [];
  }
  const generatePoliceCheckList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.policeCheckDocFile,
          fileUrl: item.policeCheckDocFileAws3Link
        }
      })
    }
    return [];
  }
  const generateWorkingChildrenList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.workingChildrenDocFile,
          fileUrl: item.workingChildrenDocFileAws3Link
        }
      })
    }
    return [];
  }
  const generateCprDocList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.cprFile,
          fileUrl: item.cprFileAws3Link
        }
      })
    }
    return [];
  }
  const generateNdisOrientationModuleList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.ndisOrientationModuleFile,
          fileUrl: item.ndisOrientationModuleFileAws3Link
        }
      })
    }
    return [];
  }

  const generateInfectionControlList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.infectionControlFile,
          fileUrl: item.infectionControlFileAws3Link
        }
      })
    }
    return [];
  }
  const generateCarRegistrationList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.carRegistrationFile,
          fileUrl: item.carRegistrationFileAws3Link
        }
      })
    }
    return [];
  }
  const generateResumeList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.resumeFile,
          fileUrl: item.resumeFileAws3Link
        }
      })
    }
    return [];
  }

  const generateComprehensiveInsuranceList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.comprehensiveInsuranceFile,
          fileUrl: item.comprehensiveInsuranceFileAws3Link
        }
      })
    }
    return [];
  }
  const generateOtherList = (list) => {
    if (list) {
      return list.map(item => {
        return {
          id: item.id,
          fileName: item.otherFile,
          fileUrl: item.otherFileAws3Link
        }
      })
    }
    return [];
  }

  // handle function
  const handleSubmitForm = (form) => {
    const params = {
      id,
      aphraRegistrationNumber: form.aphraRegistrationNumber,
      isCompletedNdisChecking: form.isCompletedNdisChecking,
      referenceRequests: form.referenceRequests
    };
    // Certification
    if (form.certificationFiles && form.certificationFiles.length > 0) {
      const fileList = [];
      for (const i in form.certificationFiles) {
        if (form.certificationFiles[i] && typeof form.certificationFiles[i] !== 'string') {
          fileList.push(form.certificationFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.certificationFileList = fileList;
      }
    }
    // APHRA registration
    if (form.aphraRegistrationFiles && form.aphraRegistrationFiles.length > 0) {
      const fileList = [];
      for (const i in form.aphraRegistrationFiles) {
        if (form.aphraRegistrationFiles[i] && typeof form.aphraRegistrationFiles[i] !== 'string') {
          fileList.push(form.aphraRegistrationFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.aphraRegistrationFileList = fileList;
      }
    }
    // Police Check
    if (form.policeCheckFiles && form.policeCheckFiles.length > 0) {
      const fileList = [];
      for (const i in form.policeCheckFiles) {
        if (form.policeCheckFiles[i] && typeof form.policeCheckFiles[i] !== 'string') {
          fileList.push(form.policeCheckFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.policeCheckFileList = fileList;
      }
    }
    // Working Children
    if (form.workingChildrenFiles && form.workingChildrenFiles.length > 0) {
      const fileList = [];
      for (const i in form.workingChildrenFiles) {
        if (form.workingChildrenFiles[i] && typeof form.workingChildrenFiles[i] !== 'string') {
          fileList.push(form.workingChildrenFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.workingWithChildrenFileList = fileList;
      }
    }
    // CPR
    if (form.cprFiles && form.cprFiles.length > 0) {
      const fileList = [];
      for (const i in form.cprFiles) {
        if (form.cprFiles[i] && typeof form.cprFiles[i] !== 'string') {
          fileList.push(form.cprFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.cprFileList = fileList;
      }
    }
    // ndisOrientationModule
    if (form.ndisOrientationModuleFiles && form.ndisOrientationModuleFiles.length > 0) {
      const fileList = [];
      for (const i in form.ndisOrientationModuleFiles) {
        if (form.ndisOrientationModuleFiles[i] && typeof form.ndisOrientationModuleFiles[i] !== 'string') {
          fileList.push(form.ndisOrientationModuleFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.ndisOrientationModuleList = fileList;
      }
    }
    // infectionControlList
    if (form.infectionControlFiles && form.infectionControlFiles.length > 0) {
      const fileList = [];
      for (const i in form.infectionControlFiles) {
        if (form.infectionControlFiles[i] && typeof form.infectionControlFiles[i] !== 'string') {
          fileList.push(form.infectionControlFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.infectionControlList = fileList;
      }
    }
    // carRegistrationList
    if (form.carRegistrationFiles && form.carRegistrationFiles.length > 0) {
      const fileList = [];
      for (const i in form.carRegistrationFiles) {
        if (form.carRegistrationFiles[i] && typeof form.carRegistrationFiles[i] !== 'string') {
          fileList.push(form.carRegistrationFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.carRegistrationList = fileList;
      }
    }
    // resumeList
    if (form.resumeFiles && form.resumeFiles.length > 0) {
      const fileList = [];
      for (const i in form.resumeFiles) {
        if (form.resumeFiles[i] && typeof form.resumeFiles[i] !== 'string') {
          fileList.push(form.resumeFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.resumeList = fileList;
      }
    }

    // comprehensiveInsuranceList
    if (form.comprehensiveInsuranceFiles && form.comprehensiveInsuranceFiles.length > 0) {
      const fileList = [];
      for (const i in form.comprehensiveInsuranceFiles) {
        if (form.comprehensiveInsuranceFiles[i] && typeof form.comprehensiveInsuranceFiles[i] !== 'string') {
          fileList.push(form.comprehensiveInsuranceFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.comprehensiveInsuranceList = fileList;
      }
    }
    // otherList
    if (form.otherFiles && form.otherFiles.length > 0) {
      const fileList = [];
      for (const i in form.otherFiles) {
        if (form.otherFiles[i] && typeof form.otherFiles[i] !== 'string') {
          fileList.push(form.otherFiles[i].file);
        }
      }
      if (fileList.length > 0) {
        params.otherList = fileList;
      }
    }
    // if (params.registrationNumberFile && !params.registrationNumberFile.name) {
    //   delete params.registrationNumberFile;
    // }
    form.certificationFiles = [];
    form.aphraRegistrationFiles = [];
    form.policeCheckFiles = [];
    form.workingChildrenFiles = [];
    form.cprFiles = [];
    form.ndisOrientationModuleFiles = [];
    form.infectionControlFiles = [];
    form.carRegistrationFiles = [];
    form.resumeFiles = [];
    form.comprehensiveInsuranceFiles = [];
    form.otherFiles = [];


    setIsSubmitting(true);
    onSubmit(params);
  };
  const handleDeleteFile = async () => {
    if (fileDeleteModal) {
      const data = { workerId: workerDetail.id };
      switch (fileDeleteModal.fieldName) {
        case 'certificationList':
          data.workerCertificationId = fileDeleteModal.id;
          break;
        case 'aphraRegistrationList':
          data.workerAphraRegistrationFileId = fileDeleteModal.id;
          break;
        case 'policeCheckList':
          data.workerPoliceCheckId = fileDeleteModal.id;
          break;
        case 'workingChildrenList':
          data.workerWorkingChildrenId = fileDeleteModal.id;
          break;
        case 'cprList':
          data.workerCprFileId = fileDeleteModal.id;
          break;
        case 'ndisOrientationModuleList':
          data.workerNdisOrientationModuleId = fileDeleteModal.id;
          break;
        case 'infectionControlList':
          data.workerInfectionControlId = fileDeleteModal.id;
          break;
        case 'carRegistrationList':
          data.workerCarRegistrationId = fileDeleteModal.id;
          break;
        case 'resumeList':
          data.workerResumeId = fileDeleteModal.id;
          break;
        case 'comprehensiveInsuranceList':
          data.workerComprehensiveInsuranceId = fileDeleteModal.id;
          break;
        case 'otherList':
          data.workerOtherId = fileDeleteModal.id;
          break;
        default:
          break;
      }
      await onDeleteFilesVerification(data);
      setFileDeleteModal(null);
    }

  };

  // Render function
  const renderFiles = (list, name) => {
    if (!list) {
      return null;
    }
    return list.map((item, index) => {
      const isFilePdf = item.fileName !== undefined ? item.fileName.includes('.pdf') ? true : false : false;
      const isFileWord = item.fileName !== undefined ? (item.fileName.includes('.doc') || item.fileName.includes('.docx')) ? true : false : false;
      const isDoc = isFilePdf || isFileWord;
      return (
        <Grid key={index} container>
          <Grid item xs={11}>
            <Field
              className={isDoc ? classes.inputFile : ''}
              component={InputUpload}
              urlFileBegin={item.fileUrl}
              name={`${name}[${index}].${isDoc ? 'fileName' : 'fileUrl'}`}
              disabled={true}
            />
          </Grid>
          <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '1rem' }}>
            <Box display="flex" justifyContent="flex-start" margin='3px 0'>
              {
                list.length > 0 &&
                <a style={{ margin: '0 5px 0 15px', color: 'red' }} title={t("button.delete")}
                  onClick={() => { setFileDeleteModal({ ...item, fieldName: name }) }}
                >
                  <Box className={classes.iconDelete} style={{ marginBottom: 0 }}>
                    <i className="fas fa-trash"></i>
                  </Box>
                </a>
              }
              <a style={{ margin: '0 5px' }} href={item.fileUrl} download={item.fileName}>
                <Box className={classes.textDownload} style={{ marginBottom: 0 }}>
                  <i className="fas fa-download"></i>
                </Box>
              </a>
            </Box>
          </Grid>
        </Grid>
      );
    });
  }
  const renderFilesWaitingSave = (setFieldValue, list, name) => {
    if (!list) {
      return null;
    }
    return list.map((item, index) => {
      var isFilePdf = false;
      var isFileWord = false;
      var isInvalidSize = false;
      if (item.file && item.file.name) {
        isFilePdf = item.file.name.includes('.pdf');
        isFileWord = item.file.name.includes('.doc') || item.file.name.includes('.docx');
        isInvalidSize = item.file.size > maxSize;
      }
      const isDoc = isFilePdf || isFileWord;
      return (
        <Grid key={index} container>
          <Grid item xs={11}>
            <Field
              className={isDoc ? classes.inputFile : ''}
              component={InputUpload}
              name={`${name}[${index}].file`}
              showBorderError={isInvalidSize}
              disabled={true}
            />
          </Grid>
          <Grid item xs={1} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '1rem' }}>
            <Box display="flex" justifyContent="flex-start" margin='3px 0'>
              <a style={{ margin: '0 5px', color: 'red' }} title={t("button.delete")}
                onClick={() => setFieldValue(name, list.filter((item, subIndex) => subIndex !== index))}
              >
                <Box className={classes.iconDelete} style={{ marginBottom: 0 }}>
                  <i className="fas fa-trash"></i>
                </Box>
              </a>
            </Box>
          </Grid>
        </Grid>
      );
    });
  }

  // validation Schema
  const referenceValidate = Yup.object().shape({
    email: Yup.string().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return Yup.string().trim()
          .required('validation.email.required')
          .email('validation.email.invalid')
          .max(255, { message: 'validation.max_length', value: 255 })
          .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces');
      }
    }),
    name: Yup.string().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return YupValidations.requiredString.max(64, "validation.max_length_64");
      }
    }),
    telephoneNumber: Yup.string().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return YupValidations.phone;
      }
    }),
    howToKnow: Yup.string().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return YupValidations.requiredString;
      }
    })
  });
  const validationSchema = Yup.object().shape({
    aphraRegistrationNumber: workerType === WORKER_TYPE.NURSE && YupValidations.requiredString,
    // registrationNumberFile: workerType === WORKER_TYPE.NURSE && Yup.mixed().required("validation.requiredFile"),
    certificationInput: Yup.array().when(['certificationFiles', 'certificationList'], (certificationFiles, certificationList, schema) => {
      if ((!certificationList || certificationList.length === 0) && (!certificationFiles || certificationFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    aphraRegistrationInput: workerType === WORKER_TYPE.NURSE && Yup.array().when(['aphraRegistrationFiles', 'aphraRegistrationList'], (aphraRegistrationFiles, aphraRegistrationList, schema) => {
      if ((!aphraRegistrationList || aphraRegistrationList.length === 0) && (!aphraRegistrationFiles || aphraRegistrationFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    policeCheckInput: Yup.array().when(['policeCheckFiles', 'policeCheckList'], (policeCheckFiles, policeCheckList, schema) => {
      if ((!policeCheckList || policeCheckList.length === 0) && (!policeCheckFiles || policeCheckFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    workingChildrenInput: workerType === WORKER_TYPE.SUPPORT_WORKER
      && Yup.array().when(['workingChildrenFiles', 'workingChildrenList'], (workingChildrenFiles, workingChildrenList, schema) => {
        if ((!workingChildrenList || workingChildrenList.length === 0) && (!workingChildrenFiles || workingChildrenFiles.length === 0)) {
          return schema.required("validation.requiredFile");
        }
      }),
    cprInput: Yup.array().when(['cprFiles', 'cprList'], (cprFiles, cprList, schema) => {
      if ((!cprList || cprList.length === 0) && (!cprFiles || cprFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    ndisOrientationModuleInput: Yup.array().when(['ndisOrientationModuleFiles', 'ndisOrientationModuleList'], (ndisOrientationModuleFiles, ndisOrientationModuleList, schema) => {
      if ((!ndisOrientationModuleList || ndisOrientationModuleList.length === 0) && (!ndisOrientationModuleFiles || ndisOrientationModuleFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    infectionControlInput: Yup.array().when(['infectionControlFiles', 'infectionControlList'], (infectionControlFiles, infectionControlList, schema) => {
      if ((!infectionControlList || infectionControlList.length === 0) && (!infectionControlFiles || infectionControlFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    carRegistrationInput: Yup.array().when(['carRegistrationFiles', 'carRegistrationList'], (carRegistrationFiles, carRegistrationList, schema) => {
      if ((!carRegistrationList || carRegistrationList.length === 0) && (!carRegistrationFiles || carRegistrationFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    resumeInput: Yup.array().when(['resumeFiles', 'resumeList'], (resumeFiles, resumeList, schema) => {
      if ((!resumeList || resumeList.length === 0) && (!resumeFiles || resumeFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    comprehensiveInsuranceInput: Yup.array().when(['comprehensiveInsuranceFiles', 'comprehensiveInsuranceList'], (comprehensiveInsuranceFiles, comprehensiveInsuranceList, schema) => {
      if ((!comprehensiveInsuranceList || comprehensiveInsuranceList.length === 0) && (!comprehensiveInsuranceFiles || comprehensiveInsuranceFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    otherInput: Yup.array().when(['otherFiles', 'otherList'], (otherFiles, otherList, schema) => {
      if ((!otherList || otherList.length === 0) && (!otherFiles || otherFiles.length === 0)) {
        return schema.required("validation.requiredFile");
      }
    }),
    referenceRequests: Yup.array().of(referenceValidate)
  });

  useEffect(() => {
    if (isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isSubmitting]);
  useEffect(() => {
    if (workerDetail && Object.keys(workerDetail).length > 0) {
      const initial = {
        aphraRegistrationNumber,
        referenceRequests: !referenceRequests || referenceRequests.length === 0
          ? [defaultReferenceRequests, defaultReferenceRequests]
          : referenceRequests.length === 1
            ? [...referenceRequests, defaultReferenceRequests]
            : referenceRequests,
        isCompletedNdisChecking: completedNdisChecking,
        certificationList: generateCertificationList(workerCertificationResponseList),
        aphraRegistrationList: generateAphraRegistrationList(workerAphraRegistrationDocResponseList),
        policeCheckList: generatePoliceCheckList(workerPoliceCheckDocResponseList),
        workingChildrenList: generateWorkingChildrenList(workerWorkingChildrenDocResponseList),
        cprList: generateCprDocList(workerCprDocResponseList),
        ndisOrientationModuleList: generateNdisOrientationModuleList(workerNdisOrientationModuleResponseList),
        infectionControlList: generateInfectionControlList(workerInfectionControlResponseList),
        carRegistrationList: generateCarRegistrationList(workerCarRegistrationResponseList),
        resumeList: generateResumeList(workerResumeResponseList),
        comprehensiveInsuranceList: generateComprehensiveInsuranceList(workerComprehensiveInsuranceResponseList),
        otherList: generateOtherList(workerOtherResponseList),

        certificationFiles: [],
        aphraRegistrationFiles: [],
        policeCheckFiles: [],
        workingChildrenFiles: [],
        cprFiles: [],
        ndisOrientationModuleFiles: [],
        infectionControlFiles: [],
        carRegistrationFiles: [],
        resumeFiles: [],
        comprehensiveInsuranceFiles: [],
        otherFiles: [],

        certificationInput: [],
        aphraRegistrationInput: [],
        policeCheckInput: [],
        workingChildrenInput: [],
        cprInput: [],
        ndisOrientationModuleInput: [],
        infectionControlInput: [],
        carRegistrationInput: [],
        resumeInput: [],
        comprehensiveInsuranceInput: [],
        otherInput: [],

      };
      validationSchema
        .isValid(initial)
        .then((valid) => {
          setTimeout(() => {
            changeProgressStatus(2, valid);
          }, 300);
        });

      setInitForm(initial);
    }
  }, [workerDetail]);

  return (
    <Formik
      enableReinitialize={true}
      onSubmit={(values, { resetForm }) => {
        resetForm();
        handleSubmitForm(values);
      }}
      initialValues={{ ...initForm }}
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue }) => {
        const showMessageInvalidSize = {
          certification: false,
          aphraRegistration: false,
          policeCheck: false,
          workingChildren: false,
          cpr: false,
          ndisOrientationModule: false,
          infectionControl: false,
          carRegistration: false,
          resume: false,
          comprehensiveInsurance: false,
          other: false

        };
        const {
          certificationFiles = [],
          aphraRegistrationFiles = [],
          policeCheckFiles = [],
          workingChildrenFiles = [],
          cprFiles = [],
          ndisOrientationModuleFiles = [],
          infectionControlFiles = [],
          carRegistrationFiles = [],
          resumeFiles = [],
          comprehensiveInsuranceFiles = [],
          otherFiles = []

        } = values;
        showMessageInvalidSize.certification = isInvalidSize(certificationFiles);
        showMessageInvalidSize.aphraRegistration = isInvalidSize(aphraRegistrationFiles);
        showMessageInvalidSize.policeCheck = isInvalidSize(policeCheckFiles);
        showMessageInvalidSize.workingChildren = isInvalidSize(workingChildrenFiles);
        showMessageInvalidSize.cpr = isInvalidSize(cprFiles);
        showMessageInvalidSize.ndisOrientationModule = isInvalidSize(ndisOrientationModuleFiles);
        showMessageInvalidSize.infectionControl = isInvalidSize(infectionControlFiles);
        showMessageInvalidSize.carRegistration = isInvalidSize(carRegistrationFiles);
        showMessageInvalidSize.resume = isInvalidSize(resumeFiles);
        showMessageInvalidSize.comprehensiveInsurance = isInvalidSize(comprehensiveInsuranceFiles);
        showMessageInvalidSize.other = isInvalidSize(otherFiles);

        const isShowMessageInvalidSize = showMessageInvalidSize.certification
          || showMessageInvalidSize.aphraRegistration || showMessageInvalidSize.workingChildren
          || showMessageInvalidSize.policeCheck || showMessageInvalidSize.cpr
          || showMessageInvalidSize.ndisOrientationModule || showMessageInvalidSize.infectionControl
          || showMessageInvalidSize.carRegistration || showMessageInvalidSize.resume
          || showMessageInvalidSize.comprehensiveInsurance || showMessageInvalidSize.other

        return (
          <Form className={`${classes.container} ${!display && 'hidden'}`}>
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("title.verification")}
                <span style={{ color: AppColor.red }}>*</span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              {t("text.uploadIdentity")}
            </Box>
            <Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  name="certificationInput"
                  disabled={disabled}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...certificationFiles];
                      if (newList.length > 10) {
                        setFieldValue("certificationInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("certificationFiles", newList);
                      setFieldValue("certificationInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, certificationFiles, 'certificationFiles')}
              {renderFiles(values.certificationList, 'certificationList')}
              {
                showMessageInvalidSize.certification &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>
            {
              workerType === WORKER_TYPE.NURSE && (
                <React.Fragment>
                  <Box display="flex" alignItems="center" marginTop={'25px'}>
                    <span>{t('label.aphraRegistrationNumber')}</span>
                  </Box>
                  <Field
                    component={AppInput}
                    name="aphraRegistrationNumber"
                    type="tel"
                    mask={maskNumber}
                    maskChar=""
                    disabled={disabled}
                  />
                  <Box>
                    {
                      !isSubmitting &&
                      <Field
                        component={InputUpload}
                        className={classes.inputFileUpload}
                        inputProps={{ multiple: true }}
                        accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        name="aphraRegistrationInput"
                        disabled={disabled}
                        showIconUpload={true}
                        resetInputAfterChangeFile={true}
                        onUploadFile={(list) => {
                          if (list && list.length > 0) {
                            const newList = [...list, ...aphraRegistrationFiles];
                            if (newList.length > 10) {
                              setFieldValue("aphraRegistrationInput", []);
                              showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                              return;
                            }
                            setFieldValue("aphraRegistrationFiles", newList);
                            setFieldValue("aphraRegistrationInput", []);
                          }
                        }}
                      />
                    }
                    {renderFilesWaitingSave(setFieldValue, aphraRegistrationFiles, 'aphraRegistrationFiles')}
                    {renderFiles(values.aphraRegistrationList, 'aphraRegistrationList')}
                    {
                      showMessageInvalidSize.aphraRegistration &&
                      <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                        {t("validation.invalidSize", { maxSize: '2MB' })}
                      </div>
                    }
                  </Box>
                </React.Fragment>
              )
            }
            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.policeCheck')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="policeCheckInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...policeCheckFiles];
                      if (newList.length > 10) {
                        setFieldValue("policeCheckInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("policeCheckFiles", newList);
                      setFieldValue("policeCheckInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, policeCheckFiles, 'policeCheckFiles')}
              {renderFiles(values.policeCheckList, 'policeCheckList')}
              {
                showMessageInvalidSize.policeCheck &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>
            {
              workerType === WORKER_TYPE.SUPPORT_WORKER && (
                <Box>
                  <Box display="flex" alignItems="center" marginTop={'25px'}>
                    <span>{t('label.workingWithChildren')}</span>
                  </Box>
                  {
                    !isSubmitting &&
                    <Field
                      component={InputUpload}
                      className={classes.inputFileUpload}
                      inputProps={{ multiple: true }}
                      accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      name="workingChildrenInput"
                      disabled={disabled}
                      showIconUpload={true}
                      resetInputAfterChangeFile={true}
                      onUploadFile={(list) => {
                        if (list && list.length > 0) {
                          const newList = [...list, ...workingChildrenFiles];
                          if (newList.length > 10) {
                            setFieldValue("workingChildrenInput", []);
                            showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                            return;
                          }
                          setFieldValue("workingChildrenFiles", newList);
                          setFieldValue("workingChildrenInput", []);
                        }
                      }}
                    />
                  }
                  {renderFilesWaitingSave(setFieldValue, workingChildrenFiles, 'workingChildrenFiles')}
                  {renderFiles(values.workingChildrenList, 'workingChildrenList')}
                  {
                    showMessageInvalidSize.workingChildren &&
                    <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                      {t("validation.invalidSize", { maxSize: '2MB' })}
                    </div>
                  }
                </Box>
              )
            }
            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.cpr')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="cprInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...cprFiles];
                      if (newList.length > 10) {
                        setFieldValue("cprInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("cprFiles", newList);
                      setFieldValue("cprInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, cprFiles, 'cprFiles')}
              {renderFiles(values.cprList, 'cprList')}
              {
                showMessageInvalidSize.cpr &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>

            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.ndisOrientationModule')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="ndisOrientationModuleInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...ndisOrientationModuleFiles];
                      if (newList.length > 10) {
                        setFieldValue("ndisOrientationModuleInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("ndisOrientationModuleFiles", newList);
                      setFieldValue("ndisOrientationModuleInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, ndisOrientationModuleFiles, 'ndisOrientationModuleFiles')}
              {renderFiles(values.ndisOrientationModuleList, 'ndisOrientationModuleList')}
              {
                showMessageInvalidSize.ndisOrientationModule &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>

            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.infectionControl')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="infectionControlInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...infectionControlFiles];
                      if (newList.length > 10) {
                        setFieldValue("infectionControlInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("infectionControlFiles", newList);
                      setFieldValue("infectionControlInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, infectionControlFiles, 'infectionControlFiles')}
              {renderFiles(values.infectionControlList, 'infectionControlList')}
              {
                showMessageInvalidSize.infectionControl &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>

            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.carRegistration')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="carRegistrationInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...carRegistrationFiles];
                      if (newList.length > 10) {
                        setFieldValue("carRegistrationInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("carRegistrationFiles", newList);
                      setFieldValue("carRegistrationInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, carRegistrationFiles, 'carRegistrationFiles')}
              {renderFiles(values.carRegistrationList, 'carRegistrationList')}
              {
                showMessageInvalidSize.carRegistration &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>

            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.resume')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="resumeInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...resumeFiles];
                      if (newList.length > 10) {
                        setFieldValue("resumeInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("resumeFiles", newList);
                      setFieldValue("resumeInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, resumeFiles, 'resumeFiles')}
              {renderFiles(values.resumeList, 'resumeList')}
              {
                showMessageInvalidSize.resume &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>

            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.comprehensiveInsurance')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="comprehensiveInsuranceInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...comprehensiveInsuranceFiles];
                      if (newList.length > 10) {
                        setFieldValue("comprehensiveInsuranceInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("comprehensiveInsuranceFiles", newList);
                      setFieldValue("comprehensiveInsuranceInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, comprehensiveInsuranceFiles, 'comprehensiveInsuranceFiles')}
              {renderFiles(values.comprehensiveInsuranceList, 'comprehensiveInsuranceList')}
              {
                showMessageInvalidSize.comprehensiveInsurance &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>

            <Box>
              <Box display="flex" alignItems="center" marginTop={'25px'}>
                <span>{t('label.other')}</span>
              </Box>
              {
                !isSubmitting &&
                <Field
                  component={InputUpload}
                  className={classes.inputFileUpload}
                  inputProps={{ multiple: true }}
                  accept="image/jpeg, image/png, application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  name="otherInput"
                  disabled={disabled}
                  showIconUpload={true}
                  resetInputAfterChangeFile={true}
                  onUploadFile={(list) => {
                    if (list && list.length > 0) {
                      const newList = [...list, ...otherFiles];
                      if (newList.length > 10) {
                        setFieldValue("otherInput", []);
                        showToastDanger(t("validation.invalidMaxNumberFile", { max: '10' }));
                        return;
                      }
                      setFieldValue("otherFiles", newList);
                      setFieldValue("otherInput", []);
                    }
                  }}
                />
              }
              {renderFilesWaitingSave(setFieldValue, otherFiles, 'otherFiles')}
              {renderFiles(values.otherList, 'otherList')}
              {
                showMessageInvalidSize.other &&
                <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '0.15rem' }}>
                  {t("validation.invalidSize", { maxSize: '2MB' })}
                </div>
              }
            </Box>

            <Box display="flex" alignItems="center" marginTop='25px'>
              <span>{t('text.ndisOrientations')}</span>
            </Box>
            <ul style={{
              listStyle: 'initial',
              paddingLeft: '2rem',
              marginBottom: '0.5rem'
            }}>
              <li>
                <span className="mr-2">{t('text.ndisMandatoryWorker')}</span>
                <span className={classes.link} onClick={() => window.open(NDIS_ORIENTATIONS_LINK.MANDATORY_WORKER)}>
                  {NDIS_ORIENTATIONS_LINK.MANDATORY_WORKER}
                </span>
              </li>
              <li>
                <span className="mr-2">{t('text.ndisScreeningCheck')}</span>
                <span className={classes.link} onClick={() => window.open(NDIS_ORIENTATIONS_LINK.SCREENING_CHECK)}>
                  {NDIS_ORIENTATIONS_LINK.SCREENING_CHECK}
                </span>
              </li>
            </ul>
            <Field
              component={AppCheckbox}
              label={t("text.confirmCompeteNDISOrientation")}
              id="isCompletedNdisChecking"
              name="isCompletedNdisChecking"
              disabled={disabled}
            />
            <FieldArray
              name="referenceRequests"
              render={() => (
                <React.Fragment>
                  {
                    values.referenceRequests && values.referenceRequests.map((item, index) => {
                      if (item.status && item.status === 'INACTIVE') {
                        return;
                      }
                      return (
                        <Box key={`ip-${index}`}>
                          <Box className="mb-3">
                            {`${t("label.reference")} ${index + 1}`}
                          </Box>
                          <Field
                            component={AppInput}
                            name={`referenceRequests[${index}].name`}
                            label={"label.name"}
                            required
                            disabled={disabled}
                          />
                          <Field
                            component={AppInput}
                            name={`referenceRequests[${index}].telephoneNumber`}
                            label={"label.telephoneNumber"}
                            type="tel"
                            mask={maskNumber}
                            maskChar=""
                            required
                            disabled={disabled}
                          />
                          <Field
                            component={AppInput}
                            name={`referenceRequests[${index}].email`}
                            label={"label.emailAddress"}
                            required
                            disabled={disabled}
                          />
                          <Field
                            component={AppInput}
                            name={`referenceRequests[${index}].howToKnow`}
                            label={"label.howToKnow"}
                            type="textarea"
                            required
                            disabled={disabled}
                          />
                        </Box>
                      );
                    })
                  }
                  <hr />
                </React.Fragment >
              )}
            />
            {
              !disabled && (
                <FormGroup className={classes.control}>
                  <AppButton
                    label={`${t("button.saveAndContinue")}`}
                    rounder
                    onClick={() => {
                      if (isShowMessageInvalidSize) {
                        showToastDanger(t("validation.invalidSize", { maxSize: '2MB' }));
                        return null;
                      }
                    }}
                    type={isShowMessageInvalidSize ? 'button' : 'submit'}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              )
            }
            {
              fileDeleteModal &&
              <AppConfirmDialog
                open={fileDeleteModal !== null}
                onConfirm={() => handleDeleteFile()}
                onClose={() => setFileDeleteModal(null)}
                title={getTitleDeleteFile(fileDeleteModal.fieldName)}
                type='danger'
                content={t('text.deleteFile', { name: fileDeleteModal.fileName })}
                minWidth={300}
              />
            }
          </Form>
        );
      }}
    </Formik>
  );
};

VerificationForm.propTypes = {
  onSubmit: PropTypes.func,
  display: PropTypes.bool,
  disabled: PropTypes.bool,
  workerDetail: PropTypes.any,
  type: PropTypes.string,
  changeProgressStatus: PropTypes.func,
  onDeleteFilesVerification: PropTypes.func,
  showToastDanger: PropTypes.func,
};

export default VerificationForm;
